export interface CompressionResult {
  compressedDataUrl: string;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  minSizeForCompression?: number;
}

const DEFAULT_OPTIONS: Required<CompressionOptions> = {
  maxWidth: 1920,
  maxHeight: 1080,
  quality: 0.8,
  minSizeForCompression: 200 * 1024,
};

export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          let { width, height } = img;

          if (width > opts.maxWidth || height > opts.maxHeight) {
            const aspectRatio = width / height;

            if (width > height) {
              width = opts.maxWidth;
              height = width / aspectRatio;
            } else {
              height = opts.maxHeight;
              width = height * aspectRatio;
            }
          }

          canvas.width = width;
          canvas.height = height;

          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);

          ctx.drawImage(img, 0, 0, width, height);

          const mimeType =
            file.type === 'image/png' && file.size > opts.minSizeForCompression
              ? 'image/jpeg'
              : file.type;

          const compressedDataUrl = canvas.toDataURL(mimeType, opts.quality);

          const originalSize = file.size;
          const compressedSize = Math.round(
            (compressedDataUrl.length * 3) / 4 -
              (compressedDataUrl.endsWith('==')
                ? 2
                : compressedDataUrl.endsWith('=')
                ? 1
                : 0)
          );

          if (compressedSize < originalSize || originalSize > opts.minSizeForCompression) {
            resolve({
              compressedDataUrl,
              originalSize,
              compressedSize,
              compressionRatio: Math.round(
                ((originalSize - compressedSize) / originalSize) * 100
              ),
            });
          } else {
            const originalDataUrl = e.target?.result as string;
            resolve({
              compressedDataUrl: originalDataUrl,
              originalSize,
              compressedSize: originalSize,
              compressionRatio: 0,
            });
          }
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
